import { motion } from "framer-motion";
import { Shield, ShieldCheck, Award, Star, Zap } from "lucide-react";

/**
 * TrustStrip — reworked with premium metallic reflections.
 * The Veteran Owned and Insured badges get a full gold-medallion treatment
 * with a slowly-sweeping specular highlight. Other badges retain a clean
 * ring style so the eye lands on the two anchor credentials.
 */
const BADGES = [
  { icon: Award,       label: "Veteran Owned",     accent: true, meta: "Est. 2013" },
  { icon: ShieldCheck, label: "Insured · $2M",     accent: true, meta: "General liability" },
  { icon: Shield,      label: "Bonded",            accent: false },
  { icon: Star,        label: "5-Star Rated",      accent: false },
  { icon: Zap,         label: "24-hr Quotes",      accent: false },
];

function Medallion({ Icon }) {
  return (
    <div className="relative">
      {/* Gold medallion ring */}
      <div
        className="relative w-11 h-11 flex items-center justify-center overflow-hidden"
        style={{
          borderRadius: "999px",
          background: "radial-gradient(circle at 30% 30%, #F5EBD6 0%, #E8D9C5 18%, #C6A87C 55%, #8A7350 100%)",
          boxShadow: "0 6px 14px rgba(0,0,0,0.55), 0 0 20px rgba(198,168,124,0.35), inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -1px 0 rgba(0,0,0,0.35)",
        }}
      >
        {/* Sweeping specular highlight */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.75) 50%, transparent 60%)",
            mixBlendMode: "screen",
          }}
          animate={{ x: ["-140%", "140%"] }}
          transition={{ duration: 4.5, ease: "easeInOut", repeat: Infinity, repeatDelay: 3.5 }}
        />
        {/* Inner ring */}
        <div className="absolute inset-[3px]" style={{
          borderRadius: "999px",
          border: "1px solid rgba(70,54,32,0.4)",
        }} />
        {/* Icon */}
        <Icon size={16} strokeWidth={1.6} style={{ color: "#3a2d18", position: "relative", zIndex: 2 }} />
      </div>
    </div>
  );
}

export default function TrustStrip() {
  return (
    <section
      className="relative"
      style={{
        background: "linear-gradient(180deg, #020204 0%, var(--bg) 100%)",
        borderTop: "1px solid var(--line)",
        borderBottom: "1px solid var(--line)",
      }}
      data-testid="trust-strip"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 py-10">
        <div className="flex flex-wrap items-center justify-center md:justify-between gap-x-10 gap-y-5">
          {BADGES.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="flex items-center gap-3.5"
                data-testid={`trust-badge-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {b.accent ? (
                  <Medallion Icon={Icon} />
                ) : (
                  <div
                    className="w-9 h-9 flex items-center justify-center"
                    style={{
                      borderRadius: "999px",
                      background: "rgba(198,168,124,0.08)",
                      border: "1px solid var(--line-gold)",
                    }}
                  >
                    <Icon size={14} style={{ color: "var(--gold)" }} strokeWidth={1.5} />
                  </div>
                )}
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: b.accent ? "var(--gold-2)" : "var(--text-2)" }}>
                    {b.label}
                  </div>
                  {b.meta && (
                    <div className="mt-0.5 font-mono text-[8.5px] uppercase tracking-[0.32em]" style={{ color: "var(--text-3)" }}>
                      {b.meta}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
